// @ts-nocheck
import { Box, Card, Code, Group, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import { ChatBubble, CommentThread, NotificationCenter } from '@/components/advanced/communication';

export default function CommunicationPage() {
  // Chat bubble data
  const otherMessage = {
    message: 'Hey team, the Q2 budget review is ready for your feedback.',
    sender: { name: 'Pam Beesly' },
    timestamp: new Date('2024-01-11T09:15:00'),
    status: 'read',
  };

  const ownMessage = {
    message: "Thanks Pam! I'll take a look this afternoon.",
    sender: { name: 'You' },
    timestamp: new Date('2024-01-11T09:18:00'),
    isOwn: true,
    status: 'read',
    reactions: [{ emoji: 'thumbup', count: 2, reacted: true }],
  };

  // Comment thread data
  const comments = [
    {
      id: 'c1',
      author: { name: 'Oscar Martinez' },
      content: 'Great work on the proposal. The cost projections look solid.',
      timestamp: new Date(Date.now() - 1000 * 60 * 45),
      likes: 4,
      liked: true,
      replies: [
        {
          id: 'c1-r1',
          author: { name: 'Angela Martin' },
          content: 'Agreed. Should we schedule a follow-up review?',
          timestamp: new Date(Date.now() - 1000 * 60 * 30),
          likes: 1,
        },
      ],
    },
    {
      id: 'c2',
      author: { name: 'Kevin Malone' },
      content: 'Can we add a breakdown by department?',
      timestamp: new Date(Date.now() - 1000 * 60 * 10),
      likes: 0,
      isEdited: true,
    },
  ];

  const currentUser = { name: 'You' };

  // Notification center data
  const notifications = [
    {
      id: 'n1',
      type: 'message',
      title: 'New message from Pam',
      message: 'The Q2 budget review is ready for your feedback.',
      timestamp: new Date(Date.now() - 1000 * 60 * 5),
      read: false,
    },
    {
      id: 'n2',
      type: 'success',
      title: 'Approval granted',
      message: 'Your expense report #4821 was approved by Finance.',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
      read: false,
      actions: [
        { label: 'View', onClick: () => {}, variant: 'primary' },
        { label: 'Dismiss', onClick: () => {} },
      ],
    },
    {
      id: 'n3',
      type: 'warning',
      title: 'Deadline approaching',
      message: 'Project "Website Redesign" is due in 2 days.',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24),
      read: true,
    },
  ];

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Communication Components</Title>
        <Text c="dimmed" size="sm">
          Components for chat, threaded discussions, and in-app notifications
        </Text>
      </Stack>

      {/* Chat Bubble */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>ChatBubble</Title>
            <Text size="sm" c="dimmed">
              Individual chat message bubble with sender, timestamp, status, and reactions
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Box maw={520}>
              <ChatBubble
                message={otherMessage.message}
                sender={otherMessage.sender}
                timestamp={otherMessage.timestamp}
                status={otherMessage.status}
                onReply={() => {}}
                onCopy={() => {}}
              />
              <ChatBubble
                message={ownMessage.message}
                sender={ownMessage.sender}
                timestamp={ownMessage.timestamp}
                isOwn={ownMessage.isOwn}
                status={ownMessage.status}
                reactions={ownMessage.reactions}
                onReply={() => {}}
                onEdit={() => {}}
                onDelete={() => {}}
                onReact={() => {}}
              />
            </Box>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<ChatBubble
  message="Hey team, the Q2 budget review is ready."
  sender={{ name: 'Pam Beesly' }}
  timestamp={new Date('2024-01-11T09:15:00')}
  status="read"
  onReply={() => {}}
  onCopy={() => {}}
/>

<ChatBubble
  message="Thanks Pam! I'll take a look this afternoon."
  sender={{ name: 'You' }}
  timestamp={new Date('2024-01-11T09:18:00')}
  isOwn
  status="read"
  reactions={[{ emoji: '👍', count: 2, reacted: true }]}
  onReply={() => {}}
  onEdit={() => {}}
  onDelete={() => {}}
  onReact={() => {}}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • message - Message text content (required)
              <br />
              • sender - {`{ name, avatar? }`} of the author (required)
              <br />
              • timestamp - Date or string when sent (required)
              <br />
              • isOwn - Render aligned right as the current user's message
              <br />
              • status - 'sending' | 'sent' | 'delivered' | 'read' | 'error'
              <br />
              • replyTo - {`{ sender, message }`} quoted reply preview
              <br />
              • reactions - Array of {`{ emoji, count, reacted? }`}
              <br />
              • onReply / onEdit / onDelete / onCopy / onReact - Action handlers
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Comment Thread */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>CommentThread</Title>
            <Text size="sm" c="dimmed">
              Nested, threaded comments with likes, replies, edit, and report actions
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <CommentThread
              comments={comments}
              currentUser={currentUser}
              onAddComment={() => {}}
              onEditComment={() => {}}
              onDeleteComment={() => {}}
              onLikeComment={() => {}}
              onReportComment={() => {}}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const comments = [
  {
    id: 'c1',
    author: { name: 'Oscar Martinez' },
    content: 'Great work on the proposal.',
    timestamp: new Date(),
    likes: 4,
    liked: true,
    replies: [
      {
        id: 'c1-r1',
        author: { name: 'Angela Martin' },
        content: 'Agreed. Should we schedule a follow-up?',
        timestamp: new Date(),
        likes: 1,
      },
    ],
  },
];

<CommentThread
  comments={comments}
  currentUser={{ name: 'You' }}
  onAddComment={(content, parentId) => {}}
  onLikeComment={(id) => {}}
  onEditComment={(id, content) => {}}
  onDeleteComment={(id) => {}}
  onReportComment={(id) => {}}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Comment Interface:</Text>
            <Code block language="typescript">
{`interface Comment {
  id: string;
  author: { name: string; avatar?: string };
  content: string;
  timestamp: Date | string;
  likes: number;
  liked?: boolean;
  replies?: Comment[];
  isEdited?: boolean;
}`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Notification Center */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>NotificationCenter</Title>
            <Text size="sm" c="dimmed">
              Bell trigger with an unread badge that opens a popover of notifications. Click the bell below.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group>
              <NotificationCenter
                notifications={notifications}
                onMarkAsRead={() => {}}
                onMarkAllAsRead={() => {}}
                onDelete={() => {}}
                onClearAll={() => {}}
                onNotificationClick={() => {}}
                onSettingsClick={() => {}}
              />
              <Text size="xs" c="dimmed">
                (2 unread — click the bell to open)
              </Text>
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const notifications = [
  {
    id: 'n1',
    type: 'message',
    title: 'New message from Pam',
    message: 'The Q2 budget review is ready.',
    timestamp: new Date(),
    read: false,
  },
  {
    id: 'n2',
    type: 'success',
    title: 'Approval granted',
    message: 'Your expense report #4821 was approved.',
    timestamp: new Date(),
    read: false,
    actions: [
      { label: 'View', onClick: () => {}, variant: 'primary' },
      { label: 'Dismiss', onClick: () => {} },
    ],
  },
];

<NotificationCenter
  notifications={notifications}
  onMarkAsRead={(id) => {}}
  onMarkAllAsRead={() => {}}
  onDelete={(id) => {}}
  onClearAll={() => {}}
  onNotificationClick={(n) => {}}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>NotificationItem Interface:</Text>
            <Code block language="typescript">
{`interface NotificationItem {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error'
      | 'message' | 'update' | 'alert';
  title: string;
  message: string;
  timestamp: Date | string;
  read?: boolean;
  avatar?: string;
  link?: string;
  actions?: {
    label: string;
    onClick: () => void;
    variant?: 'default' | 'primary' | 'danger';
  }[];
}`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Team Messaging</Text>
              <Text size="xs" c="dimmed">
                Use ChatBubble to build real-time conversation views with read receipts, replies, and reactions.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Discussions & Reviews</Text>
              <Text size="xs" c="dimmed">
                Use CommentThread for document comments, code reviews, and feedback with nested replies and likes.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>In-App Alerts</Text>
              <Text size="xs" c="dimmed">
                Use NotificationCenter in the app header for unread counts, action buttons, and activity updates.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
